import {
  useCallback,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type RefObject,
} from "react";
import { useNativeFileDrop } from "@/hooks/use-native-file-drop";
import { clipboardFiles } from "@/lib/clipboard-image";
import { readNativeDroppedFile } from "@/lib/native-clipboard-files";
import { translate } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import { useNotificationStore } from "@/stores/notification-store";
import "./composer-file-drop.css";

type FileHandler = (file: File, source: "paste" | "drop") => unknown;

/** Browser file drops bubble from every part of the composer; the desktop
 * app's OS drops reach it through `useNativeFileDrop`. */
export function useComposerFileDrop({
  rootRef,
  onFile,
  disabled = false,
}: {
  rootRef: RefObject<HTMLElement | null>;
  onFile?: FileHandler;
  disabled?: boolean;
}) {
  const [dragging, setDragging] = useState(false);
  const latest = useRef({ onFile, disabled });
  latest.current = { onFile, disabled };
  const enabled = Boolean(onFile) && !disabled;

  useNativeFileDrop(rootRef, enabled, {
    onHover: (over) => setDragging(over && !latest.current.disabled),
    onDrop: async (paths, alive) => {
      if (latest.current.disabled) return;
      const handler = latest.current.onFile;
      await Promise.all(
        paths.map(async (path) => {
          try {
            const file = await readNativeDroppedFile(path);
            if (!alive() || latest.current.disabled) return;
            handler?.(file, "drop");
          } catch (error) {
            useNotificationStore.getState().addSystemToast({
              id: `file-drop-${path}`,
              title: translate(inputBarMessages, "attachFileFailed"),
              level: "error",
              description: String(error),
            });
          }
        }),
      );
    },
  });

  const handlePaste = useCallback((event: ClipboardEvent<HTMLElement>) => {
    if (!latest.current.onFile || latest.current.disabled) return;
    const files = clipboardFiles(event.clipboardData);
    if (!files.length) return;
    event.preventDefault();
    files.forEach((file) => latest.current.onFile?.(file, "paste"));
  }, []);

  const handleDragOver = useCallback((event: DragEvent<HTMLElement>) => {
    if (!Array.from(event.dataTransfer.types).includes("Files")) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect =
      latest.current.onFile && !latest.current.disabled ? "copy" : "none";
    setDragging(Boolean(latest.current.onFile && !latest.current.disabled));
  }, []);
  const handleDrop = useCallback((event: DragEvent<HTMLElement>) => {
    if (
      !Array.from(event.dataTransfer.types).includes("Files") &&
      !event.dataTransfer.files.length
    )
      return;
    event.preventDefault();
    event.stopPropagation();
    setDragging(false);
    if (latest.current.disabled) return;
    Array.from(event.dataTransfer.files).forEach((file) =>
      latest.current.onFile?.(file, "drop"),
    );
  }, []);
  const handleDragLeave = useCallback((event: DragEvent<HTMLElement>) => {
    if (
      !(event.relatedTarget instanceof Node) ||
      !event.currentTarget.contains(event.relatedTarget)
    )
      setDragging(false);
  }, []);
  return {
    dragging,
    handlePaste,
    dropProps: {
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
}
